/*
 * <scroll-anchor-target> — in-page scroll landing target.
 *
 * Default (no data-align): inert. The `.scroll-anchor-target` CSS rule
 * (assets/custom.css) handles a top-aligned landing offset below the sticky
 * header for fragment links — no JS needed.
 *
 * data-align="center": when this anchor is the URL fragment — on cross-page
 * load (after layout settles, correcting the browser's native fragment jump)
 * and on in-page hashchange — JS positions the parent section. It centers the
 * section in the viewport when it fits clear of the sticky header; a section
 * taller than that (the common case on mobile, where it exceeds the viewport)
 * would strand the user mid-section if centered, so it falls back to
 * top-aligning the anchor below the header. Needed because CSS scroll-margin
 * can only top-align, and the native load-time jump fires before lazy content
 * above the section settles, which otherwise strands the section.
 */
if (!customElements.get('scroll-anchor-target')) {
  class ScrollAnchorTarget extends HTMLElement {
    connectedCallback() {
      if (this.dataset.align !== 'center') return;
      this.onHashChange = this.onHashChange.bind(this);
      window.addEventListener('hashchange', this.onHashChange);
      if (this.isCurrentTarget()) this.centerWhenReady();
    }

    disconnectedCallback() {
      window.removeEventListener('hashchange', this.onHashChange);
    }

    isCurrentTarget() {
      if (!this.id) return false;
      return decodeURIComponent(window.location.hash.slice(1)) === this.id;
    }

    onHashChange() {
      if (this.isCurrentTarget()) this.center();
    }

    centerWhenReady() {
      if (document.readyState === 'complete') {
        this.center();
      } else {
        window.addEventListener('load', () => this.center(), { once: true });
      }
    }

    // Public entry point. Callers may invoke center() directly to re-run the
    // landing without a hashchange — needed when the hash is already this
    // anchor's id, since assigning an unchanged hash fires no event.
    // Consumer: assets/reviews-stars-link.js.
    center() {
      const target =
        this.closest('[data-section-type]') ||
        this.closest('.shopify-section') ||
        this.parentElement;
      if (!target) return;

      // Lazy media above the section can load in bursts after the initial landing
      // and push the section down. Re-correct every frame across a settling window
      // (no early "looks stable" exit — shifts can arrive after a lull), and bail
      // the moment the user takes over scrolling.
      let cancelled = false;
      const events = ['wheel', 'touchstart', 'keydown'];
      const opts = { passive: true };
      const cancel = () => {
        cancelled = true;
        events.forEach((type) => window.removeEventListener(type, cancel, opts));
      };
      events.forEach((type) => window.addEventListener(type, cancel, opts));

      const SETTLE_MS = 2500;
      let startTime = null;
      const step = (now) => {
        if (cancelled) return;
        if (startTime === null) startTime = now;

        // Recompute each frame: lazy media inside the section can change its
        // height as it loads, flipping the fits-centered decision.
        const headerOffset = parseFloat(getComputedStyle(this).scrollMarginTop) || 0;
        const rect = target.getBoundingClientRect();
        const fitsCentered = rect.height <= window.innerHeight - headerOffset * 2;

        if (fitsCentered) {
          const desiredTop = (window.innerHeight - rect.height) / 2;
          // Only nudge when actually off-center, so a settled landing stays put.
          if (Math.abs(rect.top - desiredTop) > 2) {
            target.scrollIntoView({ block: 'center', behavior: 'instant' });
          }
        } else {
          // Taller than the viewport: top-align the anchor below the header
          // (its scroll-margin-top = --header-height supplies the offset).
          if (Math.abs(this.getBoundingClientRect().top - headerOffset) > 2) {
            this.scrollIntoView({ block: 'start', behavior: 'instant' });
          }
        }

        if (now - startTime > SETTLE_MS) {
          cancel();
          return;
        }
        requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    }
  }

  customElements.define('scroll-anchor-target', ScrollAnchorTarget);
}
